import type { DeviceSettings } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActionLink,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import { Link } from "@tanstack/react-router"
import { useCallback } from "react"

import onboardingStyles from "../Onboarding.module.scss"
import styles from "./Theme.module.scss"
import { OnboardingPageTemplate } from "src/components"
import {
  useFollowedUsers,
  usePolyglot,
} from "src/contexts"
import { useUpdateDeviceSettings } from "src/hooks"
import { Themes } from "src/pages/Settings/Theme/Themes"
import { GenericErrorToast } from "src/toasts"

const polyglotPrefix = "pages.onboarding.theme."

interface Props {
  backRoutePath: "/onboarding/phone_call_alerts"
  | "/onboarding/accept_invite/$accountId/phone_call_alerts"
  | "/data_sharing/following/accept_invite/$accountId/phone_call_alerts";
}

export function Theme(props: Props) {
  const { backRoutePath } = props

  const polyglot = usePolyglot()

  const {
    currentFollow,
    currentDeviceSettings,
    currentUserId,
  } = useFollowedUsers()

  const { showToast } = useToasterContext()

  const updateDeviceSettings = useUpdateDeviceSettings()
  const updateDeviceSettingsWithId = useCallback(
    (deviceSettings: Partial<DeviceSettings>) => {
      void (async () => {
        try {
          if (!updateDeviceSettings) {
            throw new Error("cannot update theme")
          }

          await updateDeviceSettings({
            id: currentDeviceSettings.id,
            ...deviceSettings,
          })
        } catch {
          showToast(
            "update-follows-theme",
            GenericErrorToast,
          )
        }
      })()
    },
    [
      updateDeviceSettings,
      currentDeviceSettings.id,
      showToast,
    ],
  )

  return (
    <OnboardingPageTemplate
      backRoutePath={backRoutePath}
      title={polyglot.t(`${polyglotPrefix}title`)}
    >
      <div className={onboardingStyles.container}>
        <div className={styles.description}>
          {polyglot.t(`${polyglotPrefix}summary`)}
        </div>
        <Themes
          updatedAt={currentDeviceSettings.updated_at}
          updateDeviceSettings={updateDeviceSettingsWithId}
        />
        <ActionLink
          asChild
          className={onboardingStyles.submitCta}
        >
          <Link
            to={currentFollow.is_self
              ? "/home"
              : "/home/$accountId"
            }
            params={currentFollow.is_self ? {} : { accountId: currentUserId }}
            id="finish-button"
          >
            {polyglot.t(`${polyglotPrefix}next_cta`)}
          </Link>
        </ActionLink>
      </div>
    </OnboardingPageTemplate>
  )
}
